import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  ListItem,
  OrderedList,
  Text,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { MdEmail, MdHelp, MdPhone, MdConfirmationNumber } from "react-icons/md";
import Card from "components/card/Card";
import { Mark } from "components/icons/Mark";
import { usePartnerTickets } from "hooks/usePartnerTickets";
import type { PartnerTicketCategory } from "types/partnerTicket";
import { formatDateTime } from "utils/formatDate";

const STATUS_INFO = [
  { status: "Pendente", desc: "O pedido foi recebido e aguarda separação." },
  { status: "Em separação", desc: "Os itens estão sendo preparados para envio." },
  { status: "Enviado", desc: "O pedido foi despachado e está a caminho do cliente." },
  { status: "Entregue", desc: "O cliente confirmou o recebimento." },
  { status: "Cancelado", desc: "O pedido foi cancelado e não será enviado." },
];

function ticketStatusLabel(status: string): string {
  const map: Record<string, string> = {
    open: "Aberto",
    in_progress: "Em atendimento",
    resolved: "Resolvido",
    closed: "Encerrado",
  };
  return map[status] ?? status;
}

export default function Support() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tickets, loading, creating, error, createTicket } = usePartnerTickets();

  const [category, setCategory] = useState<PartnerTicketCategory>("platform");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [relatedTaskId, setRelatedTaskId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);

  const headingColor = useColorModeValue("secondaryGray.900", "white");
  const mutedColor = useColorModeValue("secondaryGray.600", "secondaryGray.600");
  useEffect(() => {
    const taskId = searchParams.get("taskId");
    if (taskId && taskId.trim()) {
      setCategory("order");
      setRelatedTaskId(taskId.trim());
      const next = new URLSearchParams(searchParams);
      next.delete("taskId");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(false);
    const sub = subject.trim();
    const desc = description.trim();
    const taskId = relatedTaskId.trim();
    if (!sub || !desc) {
      setFormError("Preencha o assunto e a descrição.");
      return;
    }
    if (category === "order" && !taskId) {
      setFormError("Para chamados de pedido, informe o ID do pedido.");
      return;
    }
    try {
      await createTicket({
        category,
        subject: sub,
        description: desc,
        ...(category === "order" ? { relatedTaskId: taskId } : {}),
      });
      setSubject("");
      setDescription("");
      setRelatedTaskId("");
      setCategory("platform");
      setFormSuccess(true);
    } catch {
      /* erro já em formError via hook — duplicar se necessário */
    }
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading size="lg" color={headingColor} mb={1}>
          Suporte
        </Heading>
        <Text fontSize="sm" color={mutedColor}>
          Precisa de ajuda? Abra um chamado ou fale com a equipe OnlyWine.
        </Text>
      </Box>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} mb={6}>
        <Card as="form" onSubmit={handleSubmit}>
          <Heading
            size="md"
            color={headingColor}
            mb={4}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <Mark as={MdConfirmationNumber} color="brand.500" />
            Abrir chamado
          </Heading>
          <Text fontSize="xs" color={mutedColor} mb={4}>
            SLA: a equipe OnlyWine tem até <strong>2 dias úteis</strong> (segunda a sexta, horário de
            Brasília) para dar retorno a cada novo chamado.
          </Text>

          {(error || formError) && (
            <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              {formError ?? error}
            </Alert>
          )}
          {formSuccess && (
            <Alert status="success" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              Chamado registrado com sucesso.
            </Alert>
          )}

          <FormControl mb={3}>
            <FormLabel fontSize="sm" color={headingColor}>
              Tipo
            </FormLabel>
            <Select
              value={category}
              onChange={(ev) => {
                const nextCategory = ev.target.value as PartnerTicketCategory;
                setCategory(nextCategory);
                if (nextCategory !== "order") {
                  setRelatedTaskId("");
                }
              }}
              size="sm"
              borderRadius="md"
            >
              <option value="platform">Plataforma</option>
              <option value="order">Pedido</option>
            </Select>
          </FormControl>

          {category === "order" && (
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm" color={headingColor}>
                ID do pedido
              </FormLabel>
              <Input
                size="sm"
                borderRadius="md"
                placeholder="Informe o ID do pedido"
                value={relatedTaskId}
                onChange={(e) => setRelatedTaskId(e.target.value)}
              />
            </FormControl>
          )}

          <FormControl mb={3}>
            <FormLabel fontSize="sm" color={headingColor}>
              Assunto
            </FormLabel>
            <Input
              size="sm"
              borderRadius="md"
              maxLength={200}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" color={headingColor}>
              Descrição
            </FormLabel>
            <Textarea
              size="sm"
              borderRadius="md"
              rows={5}
              maxLength={5000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o problema ou a dúvida com o máximo de detalhes possível."
            />
          </FormControl>

          <Button type="submit" colorScheme="brand" isLoading={creating} loadingText="Enviando">
            Enviar chamado
          </Button>
        </Card>

        <Card>
          <Heading size="md" color={headingColor} mb={4}>
            Meus chamados
          </Heading>
          <Text fontSize="xs" color={mutedColor} mb={3}>
            Você vê apenas os chamados da sua distribuidora.
          </Text>
          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner color="brand.500" />
            </Flex>
          ) : tickets.length === 0 ? (
            <Text fontSize="sm" color={mutedColor}>
              Nenhum chamado ainda.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color={mutedColor}>Assunto</Th>
                    <Th color={mutedColor}>Tipo</Th>
                    <Th color={mutedColor}>Status</Th>
                    <Th color={mutedColor}>Aberto em</Th>
                    <Th color={mutedColor}>Prazo SLA</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tickets.map((t) => (
                    <Tr key={t.id}>
                      <Td>
                        <Text
                          as={RouterLink}
                          to={`/partner/support/${t.id}`}
                          color="brand.500"
                          fontWeight="medium"
                          fontSize="sm"
                        >
                          {t.subject}
                        </Text>
                      </Td>
                      <Td fontSize="sm" color={mutedColor}>
                        {t.category === "order" ? "Pedido" : "Plataforma"}
                      </Td>
                      <Td fontSize="sm" color={mutedColor}>
                        {ticketStatusLabel(t.status)}
                      </Td>
                      <Td fontSize="sm" color={mutedColor}>
                        {formatDateTime(t.createdAt)}
                      </Td>
                      <Td fontSize="sm" color={mutedColor}>
                        {formatDateTime(t.slaDueAt)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Card>
      </Grid>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        <Card>
          <Heading size="md" color={headingColor} mb={4} display="flex" alignItems="center" gap={2}>
            <Mark as={MdEmail} color="brand.500" />
            Contato
          </Heading>
          <Flex direction="column" gap={3} fontSize="sm">
            <Flex align="center" gap={3}>
              <Mark as={MdEmail} color="gray.400" />
              <Text color={mutedColor}>contact@onlywine.app</Text>
            </Flex>
            <Flex align="center" gap={3}>
              <Mark as={MdPhone} color="gray.400" />
              <Text color={mutedColor}>(11) 97233-9771</Text>
            </Flex>
          </Flex>
          <Text mt={4} fontSize="xs" color={mutedColor}>
            Horário de atendimento: seg–sex, 9h às 18h.
          </Text>
        </Card>

        <Card>
          <Heading size="md" color={headingColor} mb={4} display="flex" alignItems="center" gap={2}>
            <Mark as={MdHelp} color="brand.500" />
            Status dos pedidos
          </Heading>
          <Flex direction="column" gap={3}>
            {STATUS_INFO.map((item) => (
              <Box key={item.status}>
                <Text fontWeight="medium" color={headingColor}>
                  {item.status}
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  {item.desc}
                </Text>
              </Box>
            ))}
          </Flex>
        </Card>

        <GridItem colSpan={{ base: 1, lg: 2 }}>
          <Card>
            <Heading size="md" color={headingColor} mb={4}>
              Instruções para envio
            </Heading>
            <OrderedList spacing={2} fontSize="sm" color={mutedColor} pl={4}>
              <ListItem>
                Ao receber um novo pedido, verifique os itens e o endereço de entrega.
              </ListItem>
              <ListItem>
                Atualize o status para <strong>Em separação</strong> quando iniciar a preparação.
              </ListItem>
              <ListItem>
                Após despachar, atualize para <strong>Enviado</strong> e informe o código de rastreio.
              </ListItem>
              <ListItem>
                O status <strong>Entregue</strong> deve ser marcado quando o cliente receber o pedido.
              </ListItem>
              <ListItem>
                Em caso de problema, abra um chamado aqui ou entre em contato com a OnlyWine antes de
                cancelar.
              </ListItem>
            </OrderedList>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
}
